import { useCallback, useContext, useEffect, useRef, useState } from "react";

import { DASH_LINKS } from "@const/AppCardLinks";
import { formatDate, getEpoch, getTodayWithTime } from "@utils/DateFormatter";
import {
    getActionControl,
    isAllowed,
    isRoundOpen,
    parentRequestBetweenRounds,
    parentViewBetweenRounds
} from "@utils/ActionControlWindows";
import { GlobalContext } from "@components/contextProvider/ContextProvider";
import type { AppButton, AppObject } from "@/types/GlobalState";

// ─── Types ────────────────────────────────────────────────────────────────────

interface DistrictWindow {
    key: number | string;
    startDate: string;
    endDate: string;
}

interface ActionControl {
    action: string;
    differentiator: number | string;
    startDate: string;
    endDate: string;
}

interface SchoolChoiceDto {
    [key: string]: unknown;
}

interface OeStudent {
    key?: string;
    studentKey?: string | null;
    studentNumber?: string | number | null;
    schoolChoiceDetailDtos?: SchoolChoiceDto[] | null;
    [key: string]: unknown;
}

interface EligibilityEntry {
    studentNumber: string | number;
    eligible: boolean;
}

interface OeWindows {
    isAdminOfferConfirmAllowed: boolean;
    isParentOfferAllowed: boolean;
    isParentRequestAllowed: boolean;
    isParentRequestBetweenRoundsFlag: boolean;
    isParentRequestRound2Allowed: boolean;
    isParentViewBetweenRounds: boolean;
    isRound1: boolean;
    isSchoolFinalizeAllowed: boolean;
    isSchoolOfferAllowed: boolean;
    isSchoolOfferPendingAllowed: boolean;
    parentOffer: ActionControl | null;
    parentRequest: ActionControl | null;
    parentRequestRound2: ActionControl | null;
}

interface DateRange {
    endDate: string | null;
    startDate: string | null;
}

interface StatusObj {
    status: string | null;
    statusColor: string;
    statusNumber: number | null;
}

interface BodyObj {
    body: string;
    notice: string[] | null;
    subBody: string | null;
    subBodyRange: string | null;
    title: string;
}

// ─── Module-level helpers ────────────────────────────────────────────────────

const getIsEligible = (
    studentNumber: string | number | null | undefined,
    oeStudentEligibility: EligibilityEntry[] | null | undefined
): boolean => {
    try {
        if (!studentNumber || !oeStudentEligibility) return true;
        const eligibilityFind = oeStudentEligibility.find(
            (obj) => Number.parseInt(String(obj.studentNumber), 10) === Number.parseInt(String(studentNumber), 10)
        );
        return eligibilityFind?.eligible ?? true;
    } catch (error: unknown) {
        console.error("Unable to find Eligibility:\n", error);
        return false;
    }
};

const getBodyObj = (oeWindows: OeWindows, stu: OeStudent): BodyObj => {
    const { schoolChoiceDetailDtos } = stu;
    const bodyObj: BodyObj = {
        body: "OEApp_Body",
        notice: null,
        subBody: null,
        subBodyRange: null,
        title: "OEApp_Header"
    };

    if (oeWindows.parentRequest) {
        const startEpoch = getEpoch(oeWindows.parentRequest.startDate);
        const todayEpoch = getEpoch(getTodayWithTime());
        if (startEpoch !== null && todayEpoch !== null && todayEpoch < startEpoch) {
            bodyObj.notice = [
                "Global_Notice_Oe",
                formatDate(oeWindows.parentRequest.startDate, false, true) ?? "",
                "Global_Notice_Check_Back"
            ];
        }
    }

    if (
        (!schoolChoiceDetailDtos || schoolChoiceDetailDtos.length < 1) &&
        oeWindows.isParentRequestBetweenRoundsFlag &&
        oeWindows.parentRequestRound2
    ) {
        bodyObj.notice = [
            "OEApp_RoundClosed",
            formatDate(oeWindows.parentRequestRound2.startDate, true, true) ?? "",
            ""
        ];
        bodyObj.subBody = null;
        bodyObj.subBodyRange = null;
    }

    return bodyObj;
};

const getEffectiveStudentKey = (stu: OeStudent): string | null => {
    if (stu.studentKey) return stu.studentKey;
    // For external OE students without a studentNumber, stu.key may hold the OE key
    if (!stu.studentNumber && stu.key) return stu.key;
    return null;
};

const getButtonArr = (oeWindows: OeWindows, stu: OeStudent): AppButton[] => {
    const { schoolChoiceDetailDtos, studentNumber } = stu;
    const effectiveStudentKey = getEffectiveStudentKey(stu);
    const anyWindowOpen =
        oeWindows.isParentOfferAllowed ||
        oeWindows.isParentRequestAllowed ||
        oeWindows.isParentViewBetweenRounds ||
        oeWindows.isSchoolOfferAllowed ||
        oeWindows.isSchoolFinalizeAllowed;

    if (!anyWindowOpen) return [];

    let buttonArr: AppButton[] = [];

    if (schoolChoiceDetailDtos && schoolChoiceDetailDtos.length > 0) {
        buttonArr = getChoiceButtons(oeWindows, effectiveStudentKey);
    } else if (oeWindows.isParentRequestAllowed) {
        buttonArr = getRequestButtons(stu);
    }

    // The "View/Edit Student Info" buttons:
    // IC students have a studentNumber and can view their student info.
    // External guardians (new OE students without a studentNumber) can ONLY edit student info
    // during the PARENT_REQUEST window. Outside PARENT_REQUEST, external guardians cannot edit student info.
    if (studentNumber && effectiveStudentKey) {
        buttonArr.push({ label: "OEApp_Btn3", href: `${DASH_LINKS.open_enrollment}/view/${safeId(studentNumber)}` });
    } else if (effectiveStudentKey && oeWindows.isParentRequestAllowed) {
        buttonArr.push({
            label: "OEApp_Btn4",
            href: `${DASH_LINKS.open_enrollment}/edit/${safeId(effectiveStudentKey)}`
        });
    }

    return buttonArr;
};

const getStatusObj = (oeWindows: OeWindows, stu: OeStudent): StatusObj => {
    const { schoolChoiceDetailDtos } = stu;
    const effectiveStudentKey = getEffectiveStudentKey(stu);
    const statusObj: StatusObj = {
        status: null,
        statusColor: "#454545",
        statusNumber: null
    };

    if (schoolChoiceDetailDtos) {
        if (schoolChoiceDetailDtos.length === 1) {
            statusObj.status = "OEApp_1School";
            statusObj.statusColor = "#0669B3";
        } else if (schoolChoiceDetailDtos.length > 0) {
            statusObj.status = "OEApp_MoreSchools";
            statusObj.statusColor = "#0669B3";
            statusObj.statusNumber = schoolChoiceDetailDtos.length;
        } else {
            statusObj.status = "OEApp_NoSchools";
            statusObj.statusColor = "#CB321A";
        }
    }

    if (!effectiveStudentKey) {
        statusObj.status = "Global_NotStarted";
        statusObj.statusColor = "#707070";
    }

    if (
        (schoolChoiceDetailDtos?.length ?? 0) > 0 &&
        (oeWindows.isParentOfferAllowed ||
            oeWindows.isParentRequestAllowed ||
            oeWindows.isSchoolFinalizeAllowed ||
            oeWindows.isSchoolOfferAllowed ||
            oeWindows.isParentViewBetweenRounds)
    ) {
        if (oeWindows.isSchoolOfferAllowed && !oeWindows.isParentRequestRound2Allowed) {
            statusObj.status = "OEApp_SchoolsMakingOffers";
            statusObj.statusColor = "#0669B3";
            statusObj.statusNumber = null;
        }
        if (
            (oeWindows.isSchoolFinalizeAllowed || oeWindows.isParentViewBetweenRounds) &&
            !oeWindows.isParentRequestRound2Allowed
        ) {
            statusObj.status = "OEApp_SchoolsFinalizingOffers";
            statusObj.statusColor = "#0669B3";
            statusObj.statusNumber = null;
        }
    }

    return statusObj;
};

const getDateRange = (oeWindows: OeWindows, stu: OeStudent): DateRange => {
    const {
        isParentOfferAllowed,
        isParentRequestAllowed,
        isParentRequestRound2Allowed,
        parentOffer,
        parentRequest,
        parentRequestRound2
    } = oeWindows;
    const { schoolChoiceDetailDtos } = stu;
    const effectiveStudentKey = getEffectiveStudentKey(stu);

    if (isParentRequestRound2Allowed && parentRequestRound2) {
        return {
            endDate: formatDate(parentRequestRound2.endDate, true, true),
            startDate: formatDate(parentRequestRound2.startDate, true, true)
        };
    }
    if (isParentRequestAllowed && parentRequest) {
        return {
            endDate: formatDate(parentRequest.endDate, true, true),
            startDate: formatDate(parentRequest.startDate, true, true)
        };
    }
    if (isParentOfferAllowed && effectiveStudentKey && (schoolChoiceDetailDtos?.length ?? 0) > 0 && parentOffer) {
        return {
            endDate: formatDate(parentOffer.endDate, true, true),
            startDate: formatDate(parentOffer.startDate, true, true)
        };
    }

    return { endDate: null, startDate: null };
};

const getOeApp = (oeWindows: OeWindows, stu: OeStudent): AppObject => {
    const buttons = getButtonArr(oeWindows, stu);
    const statusObj = getStatusObj(oeWindows, stu);
    const bodyObj = getBodyObj(oeWindows, stu);
    const dateRange = getDateRange(oeWindows, stu);

    return {
        app: "OPEN_ENROLLMENT",
        body: bodyObj?.body,
        buttons,
        endDate: dateRange.endDate,
        icon: "OPEN_ENROLLMENT_ICON",
        moreInfo: {},
        notice: bodyObj.notice,
        startDate: dateRange.startDate,
        status: statusObj.status,
        statusColor: statusObj.statusColor,
        statusNumber: statusObj.statusNumber,
        subBody: bodyObj.subBody,
        subBodyRange: bodyObj.subBodyRange,
        subStatus: null,
        subStatusRange: null,
        title: bodyObj.title
    };
};

const getOeWindow = (
    oeActionControls: ActionControl[],
    oeActiveDistrictWindow: DistrictWindow,
    oeDistrictWindows: DistrictWindow[]
): OeWindows | null => {
    try {
        const adminOfferConfirmAllowed = isAllowed(
            "ADMIN_OFFER_CONFIRM",
            oeActionControls,
            oeActiveDistrictWindow,
            oeDistrictWindows
        );
        const schoolOfferPendingAllowed = isAllowed(
            "SCHOOL_OFFER_PENDING",
            oeActionControls,
            oeActiveDistrictWindow,
            oeDistrictWindows
        );

        return {
            isAdminOfferConfirmAllowed: adminOfferConfirmAllowed,
            isParentOfferAllowed: isAllowed(
                "PARENT_OFFER",
                oeActionControls,
                oeActiveDistrictWindow,
                oeDistrictWindows
            ),
            isParentRequestAllowed: isAllowed(
                "PARENT_REQUEST",
                oeActionControls,
                oeActiveDistrictWindow,
                oeDistrictWindows
            ),
            isParentRequestBetweenRoundsFlag: parentRequestBetweenRounds(oeActionControls, oeDistrictWindows),
            isParentRequestRound2Allowed: isAllowed(
                "PARENT_REQUEST",
                oeActionControls,
                oeDistrictWindows[1],
                oeDistrictWindows
            ),
            isParentViewBetweenRounds: parentViewBetweenRounds(oeActionControls, oeDistrictWindows),
            isRound1: isRoundOpen(oeDistrictWindows[0]),
            isSchoolFinalizeAllowed: isAllowed(
                "SCHOOL_FINALIZE",
                oeActionControls,
                oeActiveDistrictWindow,
                oeDistrictWindows
            ),
            isSchoolOfferAllowed: adminOfferConfirmAllowed || schoolOfferPendingAllowed,
            isSchoolOfferPendingAllowed: schoolOfferPendingAllowed,
            parentOffer: getActionControl("PARENT_OFFER", oeActionControls, oeActiveDistrictWindow),
            parentRequest: getActionControl("PARENT_REQUEST", oeActionControls, oeActiveDistrictWindow),
            parentRequestRound2: getActionControl("PARENT_REQUEST", oeActionControls, oeDistrictWindows[1])
        };
    } catch (error: unknown) {
        console.error("Unable to set Open Enrollment permission windows:\n", error);
    }
    return null;
};

// ─── Button Helpers ──────────────────────────────────────────────────────────

const safeId = (val: string | number | null | undefined): string => String(val ?? "").replaceAll(/[^a-zA-Z0-9_-]/g, "");

const schoolChoiceUrl = (stuKey: string | undefined): string => `${DASH_LINKS.open_enrollment}/school-choice/${stuKey}`;

const getChoiceButtons = (oeWindows: OeWindows, stuKey: string | null | undefined): AppButton[] => {
    const safeStuKey = safeId(stuKey);
    let buttons: AppButton[] = [];

    if (oeWindows.isParentRequestAllowed) {
        buttons = [{ label: "OEApp_Btn2", href: schoolChoiceUrl(safeStuKey) }];
    } else if (oeWindows.isParentOfferAllowed) {
        buttons = [{ label: "OEApp_Btn9", href: schoolChoiceUrl(safeStuKey) }];
    } else if (oeWindows.isSchoolOfferAllowed) {
        buttons = [{ label: "OEApp_Btn7", href: schoolChoiceUrl(safeStuKey) }];
    } else if (oeWindows.isParentViewBetweenRounds) {
        buttons = [{ label: "OEApp_Btn8", href: schoolChoiceUrl(safeStuKey) }];
    }

    if (
        !oeWindows.isSchoolOfferAllowed ||
        oeWindows.isParentRequestRound2Allowed ||
        oeWindows.isSchoolFinalizeAllowed
    ) {
        buttons.push({ label: "Global_MoreInfo", clickIt: true });
    }

    return buttons;
};

const getRequestButtons = (stu: OeStudent): AppButton[] => {
    const { schoolChoiceDetailDtos, studentNumber } = stu;
    const effectiveStudentKey = getEffectiveStudentKey(stu);
    const safeStudentNumber = safeId(studentNumber);
    const safeStuKey = safeId(effectiveStudentKey);
    let buttons: AppButton[] = [];

    if (!effectiveStudentKey) {
        return [{ label: "OEApp_Btn1", href: `${DASH_LINKS.open_enrollment}/view/${safeStudentNumber}` }];
    }
    if (!schoolChoiceDetailDtos || schoolChoiceDetailDtos.length < 1) {
        buttons = [{ label: "OEApp_Btn5", href: schoolChoiceUrl(safeStuKey) }];
    }

    return buttons;
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Custom hook to determine if a student's guardian has access to the Open Enrollment app tile
 * @name useOeApp
 * @param {OeStudent} student
 * @param {boolean} enabled
 * @returns {{ appObj: AppObject | null, loading: boolean }}
 */
export const useOeApp = (student: OeStudent, enabled = true): { appObj: AppObject | null; loading: boolean } => {
    const context = useContext(GlobalContext);
    const { dispatch, state } = context ?? {};
    const { oeActionControls, oeActiveDistrictWindow, oeDistrictWindows, oeStudentEligibility, studentAppObjects } =
        (state as {
            oeActionControls?: ActionControl[];
            oeActiveDistrictWindow?: DistrictWindow;
            oeDistrictWindows?: DistrictWindow[];
            oeStudentEligibility?: EligibilityEntry[] | null;
            studentAppObjects?: Record<string, Record<string, { appObj: AppObject | null }>>;
        }) || {};

    const [appObj, setAppObj] = useState<AppObject | null>(null);
    const [loading, setLoading] = useState(true);

    const studentKey = student?.key;
    const existing = studentKey ? studentAppObjects?.[studentKey]?.OPEN_ENROLLMENT : null;

    const studentAppObjectsRef = useRef(studentAppObjects);
    useEffect(() => {
        studentAppObjectsRef.current = studentAppObjects;
    }, [studentAppObjects]);

    const dispatchAppObject = useCallback(
        (obj: AppObject | null) => {
            const current = studentAppObjectsRef.current;
            const payload = {
                ...current,
                [studentKey!]: {
                    ...current?.[studentKey!],
                    OPEN_ENROLLMENT: { appObj: obj }
                }
            };
            dispatch?.({
                type: "StudentAppObjects",
                studentAppObjects: payload
            });
        },
        [dispatch, studentKey]
    );

    useEffect(() => {
        if (!enabled) {
            setLoading(false);
        }
    }, [enabled]);

    /**
     * If we have the student's apps in context, set the appObj for return
     */
    useEffect(() => {
        if (existing) {
            setAppObj(existing.appObj);
            setLoading(false);
        }
    }, [existing]);

    /**
     * Skip entirely if we've already built this app
     * Otherwise, construct the Open Enrollment appObj and update statuses per student
     * Save the appObj into context
     */
    useEffect(() => {
        if (existing) return;

        if (!oeActionControls || !oeActiveDistrictWindow || !oeDistrictWindows || !student) {
            setAppObj(null);
            setLoading(false);
            return;
        }

        const run = (): void => {
            try {
                setLoading(true);
                const oeWindows = getOeWindow(oeActionControls, oeActiveDistrictWindow, oeDistrictWindows);
                if (!oeWindows) return;

                const oeApp = getOeApp(oeWindows, student);

                if (!getIsEligible(student.studentNumber, oeStudentEligibility)) {
                    oeApp.buttons = null;
                    oeApp.notice = ["OEApp_NotEligible"];
                    oeApp.status = "OEApp_Ineligible";
                    oeApp.statusColor = "#454545";
                    oeApp.subBody = null;
                    oeApp.subBodyRange = null;
                }

                setAppObj(oeApp);
                dispatchAppObject(oeApp);
            } catch (error: unknown) {
                console.error("Unable to retrieve Open Enrollment App:\n", error);
            } finally {
                setLoading(false);
            }
        };

        if (enabled) {
            run();
        }
    }, [
        dispatchAppObject,
        enabled,
        existing,
        oeActionControls,
        oeActiveDistrictWindow,
        oeDistrictWindows,
        oeStudentEligibility,
        student,
        studentKey
    ]);

    return { appObj, loading };
};
